// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.Schedule.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import Dropdown from 'share/components/controls/Izenda.DropDown';
import Validator from 'share/components/hoc/Izenda.Validator';
import DateTimePicker from 'share/components/controls/DateTime/Izenda.DateTimePicker';
import CustomRecurrence from './Izenda.CustomRecurrence';
import Constant from 'logic/data/Izenda.ReportSubscriptionConstants';
import TimezoneConstant from 'logic/data/Izenda.TimezoneConstants';
import ScheduleConstants from 'logic/data/Izenda.ScheduleConstants';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import DashboardSchedulingConstants from 'logic/data/Izenda.DashboardSchedulingConstants';
import SCHEDULE_SEARCH_CONSTANTS from 'logic/data/Izenda.SchedulingSearchConstants';
import LicenseCheckerConstants from 'logic/data/Izenda.LicenseCheckerConstants';
import AppGlobalConstants from 'logic/data/Izenda.AppGlobalConstants';
import { isNil, gt, filter, remove } from 'lodash/fp';

const ValidatableDateTimePicker = Validator()(DateTimePicker);
const DATE_TIME_FORMAT = AppGlobalConstants.DATE_TIME_FORMAT;

@connect()
export default class Schedule extends Component {
  static propTypes = {
    data: PropTypes.object
  };

  constructor(props) {
    super(props);
    this.init();
  }

  /**
   * Init data
   */
  init() {
    const { data } = this.props;
    this.recurrenceList =
      data.type === SCHEDULE_SEARCH_CONSTANTS.INSTANCE_TYPE_VALUE.SUBSCRIBE_REPORTING_ITEM ||
      data.type === SCHEDULE_SEARCH_CONSTANTS.INSTANCE_TYPE_VALUE.SCHEDULE_REPORT
        ? ScheduleConstants.RECURRENCE_REPORTLIST
        : ScheduleConstants.RECURRENCE_ALERTLIST;
  }

  /**
   * Get subscription type
   * @return {String} subscription type
   */
  getSubscriptionType(): String {
    const props = this.props.data || {};
    return props.type;
  }

  /**
   * Get timezone
   * @return {String} timezone string
   */
  getTimezone(): String {
    const props = this.props.data || {};
    return props.timeZoneValue;
  }

  /**
   * Validate instance data.
   */
  validateInstance() {
    const {
      props,
      props: { data, validation }
    } = this;
    validation.reset();
    return validation.validate().then(success => {
      if (success) {
        props.onValidateSubscription(data).then(result => {
          validation.updateErrors(result.messages);
          return Promise.resolve('');
        });
      }
    });
  }

  /**
   * Onchange timezone dropdownlist
   * @param {Object} input
   */
  onChangeTimezone = (input: any) => {
    const props = this.props.data || {};

    props.updateProperty && props.updateProperty('timeZoneName', input.label);
    props.updateProperty && props.updateProperty('timeZoneValue', input.value);

    this.validateInstance();
  }

  /**
   * Get start date
   * @return {Object} start date
   */
  getStartDate() {
    const props = this.props.data;
    return props.startDate;
  }

  /**
   * onchange startdate picker
   * @param {Object} date
   */
  onChangeStartDate = (date: any) => {
    const props = this.props.data;
    props.updateProperty && props.updateProperty('startDate', date);

    this.validateEndDateWithStartDate(true);
  }

  /**
   * Get start time
   * @return {Object} timezone
   */
  getStartTime() {
    const props = this.props.data;
    return !isNaN(Date.parse(props.startTime)) ? props.startTime : null;
  }

  /**
   * onChange start time
   * @param {Object} date
   */
  onChangeStartTime = (data: any) => {
    const props = this.props.data;
    props.updateProperty && props.updateProperty('startTime', data);
  }

  /**
   * Get recurrence list
   * @return {Array<any>} Recurrence list
   */
  getRecurrenceList() {
    return this.recurrenceList;
  }

  /**
   * Get recurrence
   */
  getRecurrence() {
    const props = this.props.data;
    return props.recurrenceType.toString();
  }

  /**
   * onchange subscription type
   * @param {Object} data
   */
  onChangeSubscriptionType = (data: any) => {
    const props = this.props.data;
    const subscriptionType = data.value;

    this.recurrenceList =
      subscriptionType === SCHEDULE_SEARCH_CONSTANTS.INSTANCE_TYPE_VALUE.SUBSCRIBE_REPORTING_ITEM ||
      subscriptionType === DashboardSchedulingConstants.SCHEDULE_REPORT
        ? ScheduleConstants.RECURRENCE_REPORTLIST
        : ScheduleConstants.RECURRENCE_ALERTLIST;

    if (!isNil(props.updateProperty)) {
      props.updateProperty('type', subscriptionType);
      props.updateProperty('recurrenceType', this.recurrenceList[0].value);
    }
  }

  /**
   * onchange recurrence
   * @param {Object} data
   */
  onChangeRecurrence = (newData: any) => {
    const { data, validation } = this.props;
    data.updateProperty && data.updateProperty('recurrenceType', newData.value);

    validation.reset('startDate');
    validation.reset('endDate');
    this.validateEndDateWithStartDate(true);
  }

  validateEndDateWithStartDate = (isStartDate) => {
    const { data, validation } = this.props;
    const endTimeOfDay = '11:59:59 PM';
    const startDateField = 'startDate';
    const endDateField = 'endDate';

    if (data.isEndAfter || !data.isEndBy || data.isEndless) {
      return;
    }

    const startDate = new Date(data.startDate + ' ' + data.startTime);
    const endDate = new Date(data.endDate + ' ' + endTimeOfDay);

    if (gt(startDate, endDate)) {
      const message = isStartDate ? Constant.MESSAGE.M03b : Constant.MESSAGE.M03a;
      const key = isStartDate ? startDateField : endDateField;
      validation.updateErrors([
        {
          key: key,
          messages: [message]
        }
      ]);
    } else {
      validation.reset(startDateField);
      validation.reset(endDateField);
    }
  }

  // Render custom component
  renderCustomRecurrence() {
    const props = this.props.data;
    return parseInt(props.recurrenceType, 10) === parseInt(Constant.CUSTOM_RECURRENCE, 10) ? (
      <CustomRecurrence
        {...this.props}
        subscription={props}
        validateEndDateWithStartDate={this.validateEndDateWithStartDate}
      />
    ) : null;
  }

  /**
   * Render component
   */
  render() {
    const {
      data,
      authorization,
      localization,
      authorizationConstants: { PERMISSION_KEY }
    } = this.props;
    const hasAlertingLicense = authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.Alerting);
    const alertPermission = authorization.checkPrivileges([PERMISSION_KEY.ReportsActionsRegisterForAlerts]);

    let typeOptions = data.isSubscription ? Constant.SUBSCRIPTION_TYPE : DashboardSchedulingConstants.SCHEDULE_TYPE;

    if (!alertPermission) {
      typeOptions = filter( item => {
        return (
          (!data.isSubscription && item.value !== DashboardSchedulingConstants.SCHEDULE_TYPE_VALUES.scheduleAlert) ||
          (data.isSubscription && item.value !== Constant.SUBSCRIPTION_TYPE_VALUES.subscriptionAlert)
        );
      }, typeOptions);
    }

    if (!hasAlertingLicense) {
      typeOptions = remove( item => {
        return (
          item.value === DashboardSchedulingConstants.SCHEDULE_TYPE_VALUES.scheduleAlert ||
          item.value === Constant.SUBSCRIPTION_TYPE_VALUES.subscriptionAlert
        );
      }, typeOptions);
    }

    return (
      <div className='izenda-SubscriptionDesigner-Container'>
        <ScrollBox>
          <div className='form-horizontal schedule'>
            <div className='form-group'>
              <div className='col-md-3 control-label'>
                {data.isSubscription
                  ? localization.getText('SUBSCRIPTION_LABEL_SUBSCRIPTION_TYPE')
                  : localization.getText('SUBSCRIPTION_LABEL_SCHEDULING_TYPE')}
              </div>
              <div className='col-md-9'>
                <Dropdown
                  options={typeOptions}
                  value={this.getSubscriptionType()}
                  onChange={this.onChangeSubscriptionType}
                />
              </div>
            </div>
            <div className='form-group'>
              <div className='col-md-3 control-label'>{localization.getText('SUBSCRIPTION_LABEL_TIME_ZONE')}</div>
              <div className='col-md-9'>
                <Dropdown
                  options={TimezoneConstant.TIMEZONE}
                  value={this.getTimezone()}
                  onChange={this.onChangeTimezone}
                />
              </div>
            </div>
            <div className='form-group'>
              <div className='col-md-3 control-label'>{localization.getText('SUBSCRIPTION_LABEL_START_DATE')}</div>
              <div className='col-md-3'>
                <ValidatableDateTimePicker
                  time={false}
                  value={this.getStartDate()}
                  onChange={this.onChangeStartDate}
                  validations={Constant.CONTRAINTS.START_DATE}
                  storeFormat={DATE_TIME_FORMAT}
                  field='startDate'
                />
              </div>
              <div className='col-md-3 control-label'>{localization.getText('SUBSCRIPTION_LABEL_START_TIME')}</div>
              <div className='col-md-3'>
                <ValidatableDateTimePicker
                  calendar={false}
                  value={this.getStartTime()}
                  onChange={this.onChangeStartTime}
                  validations={Constant.CONTRAINTS.START_TIME}
                  storeFormat={DATE_TIME_FORMAT}
                  field='startTime'
                />
              </div>
            </div>
            <div className='form-group'>
              <div className='col-md-3 control-label'>{localization.getText('SUBSCRIPTION_LABEL_RECURRENCE')}</div>
              <div className='col-md-9'>
                <Dropdown
                  options={this.getRecurrenceList()}
                  value={this.getRecurrence()}
                  onChange={this.onChangeRecurrence}
                />
              </div>
            </div>
            <hr className='izenda-full-hr' />
            {this.renderCustomRecurrence()}
          </div>
        </ScrollBox>
      </div>
    );
  }
}


// WEBPACK FOOTER //
// ./ui/modules/Subscription/Izenda.Schedule.jsx